"use client";

import TestimonialCard from "@/app/(core)/(home)/components/TestimonialCard";
import PageSection, {
  PageSectionVariant,
} from "@/app/(core)/components/PageSection";
import SectionHeader from "@/app/(core)/components/SectionHeader";
import classes from "./reviews-section.module.css";

interface ReviewsSectionProps {
  title?: string;
  subTitle?: string;
  variant?: PageSectionVariant;
}

export default function ReviewsSection({
  title,
  subTitle,
  variant = "gray",
}: ReviewsSectionProps) {
  const reviewsMid = Math.ceil(TESTIMONIALS.length / 2);
  const reviewsTopHalf = TESTIMONIALS.slice(0, reviewsMid);
  const reviewsBottomHalf = TESTIMONIALS.slice(reviewsMid);

  return (
    <PageSection id="reviews" variant={variant}>
      <SectionHeader
        id="reviews"
        title={title ?? "Trusted Around The World."}
        subTitle={
          subTitle ??
          "85,000 students. 500+ schools. 130+ countries. Here's what they say."
        }
        align="center"
        needsLightTitle={variant === "dark"}
      />
      <div className={classes.reviewsCarouselWrapper}>
        <div className={classes.reviewsCarouselRow}>
          <div className={classes.reviewsCarouselTrack} data-direction="left">
            {reviewsTopHalf.map((t, index) => (
              <TestimonialCard
                key={`top-1-${index}`}
                quote={t.quote}
                highlightText={t.highlightText}
                name={t.name}
                role={t.role}
              />
            ))}
            {reviewsTopHalf.map((t, index) => (
              <TestimonialCard
                key={`top-2-${index}`}
                quote={t.quote}
                highlightText={t.highlightText}
                name={t.name}
                role={t.role}
                hideOnMobile={true}
              />
            ))}
          </div>
        </div>
        <div className={classes.reviewsCarouselRow}>
          <div className={classes.reviewsCarouselTrack} data-direction="right">
            {reviewsBottomHalf.map((t, index) => (
              <TestimonialCard
                key={`bot-1-${index}`}
                quote={t.quote}
                highlightText={t.highlightText}
                name={t.name}
                role={t.role}
              />
            ))}
            {reviewsBottomHalf.map((t, index) => (
              <TestimonialCard
                key={`bot-2-${index}`}
                quote={t.quote}
                highlightText={t.highlightText}
                name={t.name}
                role={t.role}
              />
            ))}
          </div>
        </div>
      </div>
    </PageSection>
  );
}

const TESTIMONIALS: {
  quote: string;
  highlightText: string[];
  name: string;
  role: string;
}[] = [
  {
    quote:
      "It's one of the best platforms I have used for me and for my students.",
    highlightText: ["best platforms", "for my students"],
    name: "Nada Radwan",
    role: "IB Teacher",
  },
  {
    quote:
      "I had an amazing experience! The platform provides exceptional resources that are both comprehensive and easy to understand... Tiber Tutor has definitely boosted my confidence.",
    highlightText: ["exceptional resources", "boosted my confidence"],
    name: "Ismaa Naveed",
    role: "IB Student",
  },
  {
    quote:
      "It's a great website that makes learning experiences interactive... The resources align perfectly with the IB curriculum, and the cram note is a game changer for last minute recall!!!",
    highlightText: ["align perfectly with the IB curriculum", "game changer"],
    name: "kayla mai",
    role: "IB Student",
  },
  {
    quote:
      "I can't express how grateful I am... The wealth of resources available have significantly enhanced my understanding of both my Chemistry HL and Biology HL.",
    highlightText: [
      "Chemistry HL and Biology HL",
      "significantly enhanced my understanding",
    ],
    name: "Joshua TAN ZPE",
    role: "IB Student",
  },
  {
    quote:
      "Tiber has allowed me to have a better understanding of multiple topics that I had questions about and guided me through High School to achieve a 95 % average.",
    highlightText: ["achieve a 95 % average"],
    name: "DB",
    role: "IB Student",
  },
  {
    quote:
      "I bought the program for my daughter, and the advance in academic results was incredible... my daughter's teacher, seeing her change, acquired it for the school so that all students could use it.",
    highlightText: ["results was incredible", "acquired it for the school"],
    name: "Montse Civi",
    role: "Parent",
  },
  {
    quote:
      "Amazing website, Tiber tutor has an amazing video explanation and it’s bio and chemistry is better than in revision village.",
    highlightText: ["better than in revision village"],
    name: "Nikita Ivanov",
    role: "IB Student",
  },
  {
    quote:
      "Great and high quality videos. Had everything I needed for revision... they are all in all great resources to get familiar with the paper 2 answering style!",
    highlightText: ["High quality videos", "paper 2 answering style"],
    name: "Richard Xun",
    role: "IB Student",
  },
  {
    quote:
      "By far the best IB sciences resource. I bought the biology membership and it was perfect for clarifying any parts of the syllabus I didn't understand.",
    highlightText: ["By far the best", "perfect for clarifying"],
    name: "Idrees Shirazi",
    role: "IB Student",
  },
  {
    quote:
      "I really enjoy the cram sheets and topic tests. The notes are easy to understand and are straight to the point.",
    highlightText: ["cram sheets", "straight to the point"],
    name: "Shope Jimoh",
    role: "IB Student",
  },
  {
    quote: "It's soo clear, amazing and helps me get 7's!!!",
    highlightText: ["helps me get 7's"],
    name: "Alon Zaikin",
    role: "IB Student",
  },
  {
    quote:
      "After using Tiber Tutor's resources for studying together with our material, there were almost never questions I did not understand or recognise.",
    highlightText: ["never questions I did not understand"],
    name: "Teodora",
    role: "IB Student",
  },
  {
    quote:
      "There's many methods to study so it allows for different learning approaches. The videos are really clear and the images really compliment all teaching.",
    highlightText: ["different learning approaches", "videos are really clear"],
    name: "Cristina Cruz",
    role: "IB Student",
  },
  {
    quote:
      "Amazing IB recourse, I use it to study for IB physics HL and the exam question function is extremely helpful.",
    highlightText: ["IB physics HL", "extremely helpful"],
    name: "Jalal",
    role: "IB Student",
  },
  {
    quote:
      "It gives you the opportunity to practice past paper questions and understand why your answer was incorrect if you got it wrong.",
    highlightText: [
      "practice past paper questions",
      "understand why your answer was incorrect",
    ],
    name: "Jessica Durham Evans",
    role: "IB Student",
  },
  {
    quote:
      "This source of revision has made my learning so much easier in preparation for the exam... I found this before exams is a godsend and a miracle.",
    highlightText: ["godsend and a miracle", "made my learning so much easier"],
    name: "A. Petrakov",
    role: "IB Student",
  },
];
